import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdicionales } from '@/presentation/hooks/useAdicionales';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreateAdicionalAction, DeleteAdicionalAction, UpdateAdicionalAction } from '@/application/actions';
import { Button, ConfirmModal, ModalShell } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import type { Adicional } from '@/domain/entities/Adicional';

/** Canales de Conexión (push va implícito con App paciente). */
const CONEXION_SERVICES = [
  { id: 'appPatient' as const, label: 'App Paciente' },
  { id: 'channelWhatsapp' as const, label: 'WhatsApp' },
] as const;

/** Acciones que el paciente puede hacer desde la app (por plan SaaS). */
const PATIENT_ACTIONS = [
  { id: 'patientConfirm' as const, label: 'Confirmar turno' },
  { id: 'patientCancel' as const, label: 'Cancelar turno' },
  { id: 'patientReschedule' as const, label: 'Reprogramar turno' },
  { id: 'patientBook' as const, label: 'Sacar turno' },
] as const;

type ConexionServiceId = (typeof CONEXION_SERVICES)[number]['id'];
type PatientActionId = (typeof PATIENT_ACTIONS)[number]['id'];
type WaMode = 'none' | 'plus' | 'pro';

type PlanCaps = {
  planAdminId: number;
  planNombre: string;
  planActivo: boolean;
  precio: number;
  appPatient: boolean;
  channelWhatsapp: boolean;
  whatsappReminderTemplateMode: WaMode;
  patientConfirm: boolean;
  patientCancel: boolean;
  patientReschedule: boolean;
  patientBook: boolean;
  canConfigurePolicies: boolean;
};

type CapabilitiesResponse = {
  adicionalId: number | null;
  plans: PlanCaps[];
};

type TierDraft = {
  id: number;
  cupoInput: string;
  precioInput: string;
  activo: boolean;
  savedCupo: number;
  savedPrecio: number;
  savedActivo: boolean;
};

/**
 * Tramo de excedente. El "desde" no se edita: sale del "hasta" del tramo
 * anterior, así la escalera queda siempre continua y sin solapamientos.
 * El último tramo no tiene tope superior.
 */
type OverageTierDraft = {
  key: string;
  hastaInput: string;
  precioInput: string;
};

type OverageTierApiDto = {
  id: number;
  minQty: number;
  maxQty: number | null;
  pricePerMessage: number;
  activo: boolean;
};

const fieldClass =
  'block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1 text-sm tabular-nums text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const selectFixedClass =
  'block w-[11rem] shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const selectEstadoClass =
  'block w-[7.5rem] shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const tableClass = 'w-full table-fixed text-left text-sm';
const thServiceClass = 'w-[12rem] px-4 py-1.5 font-medium sm:w-[14rem]';
const thPlanClass = 'min-w-[11.5rem] px-4 py-1.5 text-center font-medium';
const tdServiceClass = 'px-4 py-1.5 text-slate-800 dark:text-slate-100';
const tdPlanClass = 'px-4 py-1.5 text-center';
const cardFillClass =
  'panel-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden';
const headerBarClass =
  'flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-1.5 dark:border-gray-700';
const tableScrollClass = 'min-h-0 min-w-0 flex-1 overflow-auto';
const tableBodyClass = 'min-w-full text-left text-sm';
const theadStickyClass =
  'panel-thead sticky top-0 z-10 text-[11px] uppercase tracking-wide text-slate-500';

type ConexionTabKey = 'escalones' | 'referencia' | 'disponibilidad' | 'excedentes';

const CONEXION_TABS: { key: ConexionTabKey; label: string }[] = [
  { key: 'escalones', label: 'Escalones' },
  { key: 'referencia', label: 'Referencia + plan' },
  { key: 'disponibilidad', label: 'Disponibilidad por plan' },
  { key: 'excedentes', label: 'Excedentes' },
];

function formatMoney(value: number): string {
  return `$${value.toLocaleString('es-AR')}`;
}

/** Digitos sin ceros a la izquierda ("" permitido mientras se escribe). */
function sanitizeDigitsInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  return digits.replace(/^0+(?=\d)/, '');
}

function parseDigitsOrZero(raw: string): number {
  const cleaned = sanitizeDigitsInput(raw);
  if (cleaned === '') return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function toTierDraft(a: Adicional): TierDraft | null {
  const cupo = a.cupoMensajes ?? 0;
  if (cupo <= 0) return null;
  return {
    id: a.id,
    cupoInput: String(cupo),
    precioInput: String(Math.round(a.precioUnitario)),
    activo: a.isActive(),
    savedCupo: cupo,
    savedPrecio: a.precioUnitario,
    savedActivo: a.isActive(),
  };
}

let overageDraftSeq = 0;

function newOverageDraftKey(): string {
  overageDraftSeq += 1;
  return `overage-${overageDraftSeq}`;
}

function toOverageDrafts(tiers: OverageTierApiDto[]): OverageTierDraft[] {
  return tiers
    .filter((t) => t.activo)
    .slice()
    .sort((a, b) => a.minQty - b.minQty)
    .map((t) => ({
      key: newOverageDraftKey(),
      hastaInput: t.maxQty == null ? '' : String(t.maxQty),
      precioInput: String(Math.round(t.pricePerMessage)),
    }));
}

/** "Desde" de cada tramo: 1 el primero, y el siguiente al "hasta" del anterior. */
function overageMinFor(drafts: OverageTierDraft[], index: number): number {
  let min = 1;
  for (let i = 0; i < index; i += 1) {
    min = parseDigitsOrZero(drafts[i].hastaInput) + 1;
  }
  return min;
}

function snapshotOverage(drafts: OverageTierDraft[]): string {
  return JSON.stringify(drafts.map((d) => [d.hastaInput, d.precioInput]));
}

/** Al editar el "desde" de un tramo, el "hasta" del anterior se ajusta para no dejar hueco. */
function withDesdeAt(
  drafts: OverageTierDraft[],
  index: number,
  rawDesde: string,
): OverageTierDraft[] {
  if (index <= 0) return drafts;
  const newDesde = parseDigitsOrZero(rawDesde);
  return drafts.map((row, i) => {
    if (i !== index - 1) return row;
    const prevMin = overageMinFor(drafts, i);
    if (newDesde <= prevMin) {
      return { ...row, hastaInput: String(prevMin) };
    }
    return { ...row, hastaInput: String(newDesde - 1) };
  });
}

function servicesLabel(plan: PlanCaps): string {
  const parts = [
    ...CONEXION_SERVICES.filter((s) => plan[s.id]).map((s) => s.label),
    ...(plan.appPatient ? ['Push'] : []),
  ];
  const actions = PATIENT_ACTIONS.filter((a) => plan[a.id]).map((a) => a.label);
  if (actions.length) parts.push(actions.join(', '));
  return parts.length ? parts.join(' + ') : '—';
}

function snapshotPlans(plans: PlanCaps[]): string {
  return JSON.stringify(
    plans.map((p) => ({
      id: p.planAdminId,
      app: p.appPatient,
      wa: p.channelWhatsapp,
      mode: p.whatsappReminderTemplateMode === 'none' ? 'none' : 'plus',
      confirm: p.patientConfirm,
      cancel: p.patientCancel,
      reschedule: p.patientReschedule,
      book: p.patientBook,
    })),
  );
}

function normalizePlan(raw: Partial<PlanCaps> & { planAdminId: number }): PlanCaps {
  const appPatient = Boolean(raw.appPatient);
  return {
    planAdminId: raw.planAdminId,
    planNombre: raw.planNombre ?? `Plan ${raw.planAdminId}`,
    planActivo: raw.planActivo !== false,
    precio: Number(raw.precio) || 0,
    appPatient,
    channelWhatsapp: Boolean(raw.channelWhatsapp),
    whatsappReminderTemplateMode:
      raw.whatsappReminderTemplateMode === 'none' ? 'none' : 'plus',
    patientConfirm: appPatient && Boolean(raw.patientConfirm),
    patientCancel: appPatient && Boolean(raw.canConfigurePolicies) && Boolean(raw.patientCancel),
    patientReschedule:
      appPatient && Boolean(raw.canConfigurePolicies) && Boolean(raw.patientReschedule),
    patientBook: appPatient && Boolean(raw.patientBook),
    canConfigurePolicies: Boolean(raw.canConfigurePolicies),
  };
}

/** Toggle visible y clickeable (ancho fijo para no reflow de tablas). */
function CapToggle({
  checked,
  disabled,
  onToggle,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={[
        'inline-flex h-7 w-11 shrink-0 items-center justify-center rounded-md border text-xs font-semibold transition',
        disabled
          ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400 dark:border-gray-700 dark:bg-gray-800/60 dark:text-slate-500'
          : checked
            ? 'cursor-pointer border-teal-600 bg-teal-600 text-white hover:bg-teal-700'
            : 'cursor-pointer border-slate-300 bg-white text-slate-600 hover:border-teal-500 hover:text-teal-700 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200',
      ].join(' ')}
    >
      {checked ? 'Sí' : 'No'}
    </button>
  );
}

/**
 * Coofit Conexión — escalones (cupo WA incluido), caps por plan SaaS y precios de recarga.
 * La asignación a clínicas es por ficha de clínica (un escalón activo).
 */
export const CoofitConexionPage: React.FC = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const { canAdminister } = useAuth();

  const { adicionales, isLoading, error, refetch } = useAdicionales(undefined, undefined);

  const conexionItems = useMemo(
    () =>
      adicionales
        .filter((a) => a.isCoofitConexion())
        .slice()
        .sort((a, b) => (a.cupoMensajes ?? 0) - (b.cupoMensajes ?? 0)),
    [adicionales],
  );

  const [tiers, setTiers] = useState<TierDraft[]>([]);
  const [overageTiers, setOverageTiers] = useState<OverageTierDraft[]>([]);
  const [savedOverageSnapshot, setSavedOverageSnapshot] = useState('');
  const [overageLoading, setOverageLoading] = useState(false);
  const [overageError, setOverageError] = useState<string | null>(null);
  const [savingOverage, setSavingOverage] = useState(false);
  const [planCaps, setPlanCaps] = useState<PlanCaps[]>([]);
  const [savedCapsSnapshot, setSavedCapsSnapshot] = useState('');
  const [capsLoading, setCapsLoading] = useState(false);
  const [capsError, setCapsError] = useState<string | null>(null);
  const [savingTiers, setSavingTiers] = useState(false);
  const [savingCaps, setSavingCaps] = useState(false);
  const [isCreateTierOpen, setIsCreateTierOpen] = useState(false);
  const [newTierCupoInput, setNewTierCupoInput] = useState('');
  const [newTierPrecioInput, setNewTierPrecioInput] = useState('');
  const [creatingTier, setCreatingTier] = useState(false);
  const [tierToDelete, setTierToDelete] = useState<TierDraft | null>(null);
  const [deletingTier, setDeletingTier] = useState(false);
  const [activeTab, setActiveTab] = useState<ConexionTabKey>('escalones');

  useEffect(() => {
    setTiers(conexionItems.map(toTierDraft).filter((x): x is TierDraft => x != null));
  }, [conexionItems]);

  const loadOverageTiers = async () => {
    setOverageLoading(true);
    setOverageError(null);
    try {
      const response = await httpClient.get<OverageTierApiDto[]>('/admin/whatsapp-overage-tiers');
      const drafts = toOverageDrafts(response.data ?? []);
      setOverageTiers(drafts);
      setSavedOverageSnapshot(snapshotOverage(drafts));
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
            )
          : '';
      setOverageError(
        axiosMsg ||
          (err instanceof Error ? err.message : 'No se pudieron cargar los tramos de excedente'),
      );
    } finally {
      setOverageLoading(false);
    }
  };

  useEffect(() => {
    void loadOverageTiers();
  }, []);

  const loadCapabilities = async () => {
    setCapsLoading(true);
    setCapsError(null);
    try {
      const response = await httpClient.get<CapabilitiesResponse>(
        '/admin/adicionales/coofit-conexion/capabilities',
      );
      const plans = (response.data.plans ?? []).map(normalizePlan);
      setPlanCaps(plans);
      setSavedCapsSnapshot(snapshotPlans(plans));
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
                '',
            )
          : '';
      setCapsError(
        axiosMsg ||
          (err instanceof Error ? err.message : 'No se pudieron cargar las capacidades'),
      );
    } finally {
      setCapsLoading(false);
    }
  };

  useEffect(() => {
    void loadCapabilities();
  }, []);

  const included = useMemo(() => {
    return {
      appPatient: planCaps.some((p) => p.appPatient),
      channelWhatsapp: planCaps.some((p) => p.channelWhatsapp),
    };
  }, [planCaps]);

  const tiersDirty = tiers.some((t) => {
    const cupo = parseDigitsOrZero(t.cupoInput);
    const precio = parseDigitsOrZero(t.precioInput);
    return cupo !== t.savedCupo || precio !== t.savedPrecio || t.activo !== t.savedActivo;
  });
  const overageDirty = snapshotOverage(overageTiers) !== savedOverageSnapshot;
  const capsDirty = snapshotPlans(planCaps) !== savedCapsSnapshot;

  const toggleIncluded = (serviceId: ConexionServiceId) => {
    setPlanCaps((prev) => {
      const nextOn = !prev.some((p) => p[serviceId]);
      return prev.map((p) => {
        if (serviceId === 'appPatient') {
          return {
            ...p,
            appPatient: nextOn,
            patientConfirm: nextOn ? p.patientConfirm : false,
            patientCancel: nextOn ? p.patientCancel : false,
            patientReschedule: nextOn ? p.patientReschedule : false,
            patientBook: nextOn ? p.patientBook : false,
          };
        }
        return {
          ...p,
          channelWhatsapp: nextOn,
          whatsappReminderTemplateMode: nextOn ? p.whatsappReminderTemplateMode : 'none',
        };
      });
    });
  };

  const togglePlanService = (planAdminId: number, serviceId: ConexionServiceId) => {
    setPlanCaps((prev) =>
      prev.map((p) => {
        if (p.planAdminId !== planAdminId) return p;
        const next = !p[serviceId];
        if (serviceId === 'appPatient') {
          return {
            ...p,
            appPatient: next,
            patientConfirm: next ? p.patientConfirm : false,
            patientCancel: next ? p.patientCancel : false,
            patientReschedule: next ? p.patientReschedule : false,
            patientBook: next ? p.patientBook : false,
          };
        }
        return {
          ...p,
          channelWhatsapp: next,
          whatsappReminderTemplateMode: next ? p.whatsappReminderTemplateMode : 'none',
        };
      }),
    );
  };

  const togglePlanAction = (planAdminId: number, actionId: PatientActionId) => {
    setPlanCaps((prev) =>
      prev.map((p) => {
        if (p.planAdminId !== planAdminId || !p.appPatient) return p;
        if (
          (actionId === 'patientCancel' || actionId === 'patientReschedule') &&
          !p.canConfigurePolicies
        ) {
          return p;
        }
        return { ...p, [actionId]: !p[actionId] };
      }),
    );
  };

  const setPlanMode = (planAdminId: number, mode: WaMode) => {
    setPlanCaps((prev) =>
      prev.map((p) =>
        p.planAdminId === planAdminId ? { ...p, whatsappReminderTemplateMode: mode } : p,
      ),
    );
  };

  const handleSaveTiers = async () => {
    if (!canAdminister || !tiersDirty) return;
    const parsed = tiers.map((tier) => ({
      tier,
      cupo: parseDigitsOrZero(tier.cupoInput),
      precio: parseDigitsOrZero(tier.precioInput),
    }));
    for (const row of parsed) {
      if (row.precio < 0) {
        showError(`Precio inválido en escalón ${row.tier.id}`);
        return;
      }
      if (row.cupo < 1) {
        showError('Cada escalón activo necesita cupo de mensajes >= 1');
        return;
      }
    }
    const cupos = parsed.filter((r) => r.tier.activo).map((r) => r.cupo);
    if (new Set(cupos).size !== cupos.length) {
      showError('No puede haber dos escalones activos con el mismo cupo de mensajes');
      return;
    }
    setSavingTiers(true);
    try {
      const update = container.get<UpdateAdicionalAction>(TYPES.UpdateAdicionalAction);
      for (const { tier, cupo, precio } of parsed) {
        if (
          cupo === tier.savedCupo &&
          precio === tier.savedPrecio &&
          tier.activo === tier.savedActivo
        ) {
          continue;
        }
        await update.execute(tier.id, {
          nombre: `Coofit Conexión ${cupo}`,
          descripcion: `App paciente, push y WhatsApp. Incluye ${cupo} mensajes/mes.`,
          precioUnitario: precio,
          unidad: 'mes',
          tipo: 'COOFIT_CONEXION',
          cupoMensajes: cupo,
          esRecurrente: true,
          activo: tier.activo,
        });
      }
      success('Escalones Conexión guardados');
      refetch();
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
                '',
            )
          : '';
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setSavingTiers(false);
    }
  };

  const handleCreateTier = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canAdminister) return;
    if (tiersDirty) {
      showError(t('pages.coofitConexion.tiers.savePendingBeforeCreate'));
      return;
    }

    const cupo = parseDigitsOrZero(newTierCupoInput);
    const precio = parseDigitsOrZero(newTierPrecioInput);
    if (cupo < 1) {
      showError(t('pages.coofitConexion.tiers.validation.quotaRequired'));
      return;
    }
    if (precio < 0) {
      showError(t('pages.coofitConexion.tiers.validation.priceMin'));
      return;
    }
    if (tiers.some((tier) => parseDigitsOrZero(tier.cupoInput) === cupo)) {
      showError(t('pages.coofitConexion.tiers.validation.duplicateQuota'));
      return;
    }

    setCreatingTier(true);
    try {
      const create = container.get<CreateAdicionalAction>(TYPES.CreateAdicionalAction);
      await create.execute({
        nombre: `Coofit Conexión ${cupo}`,
        descripcion: `App paciente, push y WhatsApp. Incluye ${cupo} mensajes/mes.`,
        precioUnitario: precio,
        unidad: 'mes',
        tipo: 'COOFIT_CONEXION',
        cupoMensajes: cupo,
        esRecurrente: true,
        activo: true,
      });
      success(t('pages.coofitConexion.tiers.createdSuccess'));
      setIsCreateTierOpen(false);
      setNewTierCupoInput('');
      setNewTierPrecioInput('');
      refetch();
      void loadCapabilities();
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
                '',
            )
          : '';
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setCreatingTier(false);
    }
  };

  const handleDeleteTier = async () => {
    if (!tierToDelete || !canAdminister) return;
    if (tiersDirty) {
      showError(t('pages.coofitConexion.tiers.savePendingBeforeDelete'));
      return;
    }
    setDeletingTier(true);
    try {
      const remove = container.get<DeleteAdicionalAction>(TYPES.DeleteAdicionalAction);
      await remove.execute(tierToDelete.id);
      success(t('pages.coofitConexion.tiers.deletedSuccess'));
      setTierToDelete(null);
      refetch();
      void loadCapabilities();
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
                '',
            )
          : '';
      const status =
        err && typeof err === 'object' && 'response' in err
          ? Number((err as { response?: { status?: number } }).response?.status ?? 0)
          : 0;
      showError(
        status === 409
          ? t('pages.coofitConexion.tiers.deleteBlocked')
          : axiosMsg || (err instanceof Error ? err.message : t('common.error')),
      );
    } finally {
      setDeletingTier(false);
    }
  };

  const addOverageTier = () => {
    setOverageTiers((prev) => {
      // El nuevo tramo se agrega al final, así que el anterior deja de ser
      // el ilimitado y necesita un "hasta" propuesto.
      const next = prev.map((row, index) =>
        index === prev.length - 1 && row.hastaInput.trim() === ''
          ? { ...row, hastaInput: String(overageMinFor(prev, index) + 99) }
          : row,
      );
      return [...next, { key: newOverageDraftKey(), hastaInput: '', precioInput: '' }];
    });
  };

  const removeOverageTier = (key: string) => {
    setOverageTiers((prev) => {
      const next = prev.filter((row) => row.key !== key);
      if (next.length === 0) return next;
      // El último siempre queda sin tope superior.
      return next.map((row, index) =>
        index === next.length - 1 ? { ...row, hastaInput: '' } : row,
      );
    });
  };

  const handleSaveOverageTiers = async () => {
    if (!canAdminister || !overageDirty) return;
    if (overageTiers.length === 0) {
      showError('Definí al menos un tramo de excedente');
      return;
    }

    const payload: { minQty: number; maxQty: number | null; pricePerMessage: number }[] = [];
    for (let index = 0; index < overageTiers.length; index += 1) {
      const row = overageTiers[index];
      const isLast = index === overageTiers.length - 1;
      const minQty = overageMinFor(overageTiers, index);
      const maxQty = isLast ? null : parseDigitsOrZero(row.hastaInput);
      const precio = parseDigitsOrZero(row.precioInput);

      if (minQty < 1) {
        showError('Revisá los tramos: el "desde" quedó inválido');
        return;
      }
      if (maxQty != null && maxQty < minQty) {
        showError(`El tramo que arranca en ${minQty} necesita un "hasta" mayor o igual a ${minQty}`);
        return;
      }
      if (precio < 0) {
        showError('El precio por mensaje no puede ser negativo');
        return;
      }
      payload.push({ minQty, maxQty, pricePerMessage: precio });
    }

    setSavingOverage(true);
    try {
      const saved = await httpClient.put<OverageTierApiDto[]>('/admin/whatsapp-overage-tiers', {
        tiers: payload,
      });
      const drafts = toOverageDrafts(saved.data ?? []);
      setOverageTiers(drafts);
      setSavedOverageSnapshot(snapshotOverage(drafts));
      success('Tramos de excedente guardados');
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
                '',
            )
          : '';
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setSavingOverage(false);
    }
  };

  const handleSaveCaps = async () => {
    if (!canAdminister || !capsDirty) return;
    if (tiers.length === 0) {
      showError('No hay escalones Conexión en el catálogo. Corré el migrate del Panel.');
      return;
    }
    setSavingCaps(true);
    try {
      const saved = await httpClient.put<CapabilitiesResponse>(
        '/admin/adicionales/coofit-conexion/capabilities',
        {
          plans: planCaps.map((p) => ({
            planAdminId: p.planAdminId,
            appPatient: p.appPatient,
            channelWhatsapp: p.channelWhatsapp,
            whatsappReminderTemplateMode:
              p.whatsappReminderTemplateMode === 'none' ? 'none' : 'plus',
            patientConfirm: p.patientConfirm,
            patientCancel: p.patientCancel,
            patientReschedule: p.patientReschedule,
            patientBook: p.patientBook,
          })),
        },
      );
      const plans = (saved.data.plans ?? planCaps).map(normalizePlan);
      setPlanCaps(plans);
      setSavedCapsSnapshot(snapshotPlans(plans));
      success('Disponibilidad por plan guardada');
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
                '',
            )
          : '';
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setSavingCaps(false);
    }
  };

  if (isLoading && adicionales.length === 0) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-start">
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      </div>
    );
  }

  const readOnly = !canAdminister;
  const visiblePlans = planCaps.filter((p) => p.planAdminId !== 2);
  const entryTier = tiers.find((t) => t.activo) ?? tiers[0] ?? null;
  const entryTierCupo = entryTier ? parseDigitsOrZero(entryTier.cupoInput) : 0;
  const entryTierPrecio = entryTier ? parseDigitsOrZero(entryTier.precioInput) : 0;
  const deleteTierCupo = tierToDelete ? parseDigitsOrZero(tierToDelete.cupoInput) : 0;
  const deleteTierName =
    deleteTierCupo > 0 ? `Coofit Conexión ${deleteTierCupo.toLocaleString('es-AR')}` : 'este escalón';
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      {(error || capsError) && (
        <div className="shrink-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error?.message || capsError}
        </div>
      )}

      <nav
        className="flex shrink-0 gap-0.5 overflow-x-auto border-b border-slate-200 dark:border-gray-700"
        aria-label="Secciones de Coofit Conexión"
      >
        {CONEXION_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`whitespace-nowrap border-b-2 px-2.5 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
              activeTab === tab.key
                ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'escalones' && (
      <section className={cardFillClass}>
        <div className={headerBarClass}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Escalones Conexión
          </h2>
          {canAdminister ? (
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                disabled={savingTiers || creatingTier || deletingTier}
                onClick={() => {
                  if (tiersDirty) {
                    showError(t('pages.coofitConexion.tiers.savePendingBeforeCreate'));
                    return;
                  }
                  setIsCreateTierOpen(true);
                }}
              >
                Nuevo escalón
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={savingTiers}
                disabled={!tiersDirty || creatingTier || deletingTier}
                onClick={() => void handleSaveTiers()}
              >
                Guardar escalones
              </Button>
            </div>
          ) : null}
        </div>
        {tiers.length === 0 ? (
          <p className="px-4 py-4 text-sm text-slate-500">
            No hay escalones. Corré <code className="text-xs">npm run migrate</code> en el API del
            Panel.
          </p>
        ) : (
          <div className={tableScrollClass}>
            <table className={tableBodyClass}>
              <thead className={theadStickyClass}>
                <tr>
                  <th className="px-4 py-1.5 font-medium">Escalón</th>
                  <th className="px-4 py-1.5 font-medium">Msgs / mes</th>
                  <th className="px-4 py-1.5 font-medium">Precio mensual</th>
                  <th className="px-4 py-1.5 font-medium">Estado</th>
                  {canAdminister ? <th className="px-4 py-1.5 font-medium">Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => {
                  const cupoNum = parseDigitsOrZero(tier.cupoInput);
                  return (
                  <tr
                    key={tier.id}
                    className="border-t border-slate-100 dark:border-gray-700"
                  >
                    <td className="px-4 py-1.5 font-medium text-slate-900 dark:text-white">
                      Coofit Conexión{' '}
                      {cupoNum > 0 ? cupoNum.toLocaleString('es-AR') : '…'}
                    </td>
                    <td className="px-4 py-1.5">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={tier.cupoInput}
                        disabled={readOnly}
                        onChange={(e) => {
                          const cupoInput = sanitizeDigitsInput(e.target.value);
                          setTiers((prev) =>
                            prev.map((x) => (x.id === tier.id ? { ...x, cupoInput } : x)),
                          );
                        }}
                        className={`${fieldClass} max-w-[7.5rem]`}
                      />
                    </td>
                    <td className="px-4 py-1.5">
                      <div className="relative max-w-[9rem]">
                        <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-slate-400">
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={tier.precioInput}
                          disabled={readOnly}
                          onChange={(e) => {
                            const precioInput = sanitizeDigitsInput(e.target.value);
                            setTiers((prev) =>
                              prev.map((x) => (x.id === tier.id ? { ...x, precioInput } : x)),
                            );
                          }}
                          className={`${fieldClass} pl-6`}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-1.5">
                      <select
                        value={tier.activo ? 'activo' : 'pausado'}
                        disabled={readOnly}
                        onChange={(e) => {
                          const activo = e.target.value === 'activo';
                          setTiers((prev) =>
                            prev.map((x) => (x.id === tier.id ? { ...x, activo } : x)),
                          );
                        }}
                        className={selectEstadoClass}
                      >
                        <option value="activo">Activo</option>
                        <option value="pausado">Pausado</option>
                      </select>
                    </td>
                    {canAdminister ? (
                      <td className="px-4 py-1.5">
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={savingTiers || creatingTier || deletingTier}
                          onClick={() => {
                            if (tiersDirty) {
                              showError(t('pages.coofitConexion.tiers.savePendingBeforeDelete'));
                              return;
                            }
                            setTierToDelete(tier);
                          }}
                        >
                          Eliminar
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {activeTab === 'referencia' && (
      <section className={cardFillClass}>
        <div className={headerBarClass}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Referencia plan + Conexión entrada
            {entryTier && entryTierCupo > 0
              ? ` · ${entryTierCupo.toLocaleString('es-AR')}`
              : ''}
          </h2>
        </div>
        <div className={tableScrollClass}>
          <table className={tableClass}>
            <thead className={theadStickyClass}>
              <tr>
                <th className="w-[7rem] px-4 py-1.5 font-medium">Plan</th>
                <th className="w-[7rem] px-4 py-1.5 font-medium">Precio plan</th>
                <th className="w-[7rem] px-4 py-1.5 font-medium">+ Conexión</th>
                <th className="w-[7rem] px-4 py-1.5 font-medium">Total</th>
                <th className="px-4 py-1.5 font-medium">Servicios (caps)</th>
              </tr>
            </thead>
            <tbody>
              {visiblePlans.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-sm text-slate-500">
                    No hay planes en el catálogo.
                  </td>
                </tr>
              ) : (
                visiblePlans.map((plan) => {
                  const conexionPrecio = entryTierPrecio;
                  const total = plan.precio + conexionPrecio;
                  return (
                    <tr
                      key={plan.planAdminId}
                      className="border-t border-slate-100 dark:border-gray-700"
                    >
                      <td className="px-4 py-1.5 font-medium text-slate-900 dark:text-white">
                        <span className="block truncate">{plan.planNombre}</span>
                      </td>
                      <td className="px-4 py-1.5 tabular-nums text-slate-700 dark:text-slate-200">
                        {formatMoney(plan.precio)}
                      </td>
                      <td className="px-4 py-1.5 tabular-nums text-slate-700 dark:text-slate-200">
                        +{formatMoney(conexionPrecio)}
                      </td>
                      <td className="px-4 py-1.5 font-semibold tabular-nums text-slate-900 dark:text-white">
                        {formatMoney(total)}
                      </td>
                      <td className="px-4 py-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <span className="block truncate" title={servicesLabel(plan)}>
                          {servicesLabel(plan)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'disponibilidad' && (
      <section className={cardFillClass}>
        <div className={headerBarClass}>
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Disponibilidad por plan SaaS
            </h2>
            {CONEXION_SERVICES.map((s) => (
              <label
                key={s.id}
                className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-700 dark:text-slate-200"
              >
                <input
                  type="checkbox"
                  checked={included[s.id]}
                  disabled={readOnly || visiblePlans.length === 0}
                  onChange={() => toggleIncluded(s.id)}
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 dark:border-gray-600"
                />
                {s.label}
              </label>
            ))}
            <span
              className={`inline-flex items-center text-xs ${
                included.appPatient ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              + Push
            </span>
            {capsLoading ? (
              <span className="text-[11px] text-slate-400">Cargando…</span>
            ) : null}
            {readOnly ? (
              <span className="text-xs text-amber-700 dark:text-amber-300">Solo lectura</span>
            ) : null}
          </div>
          {canAdminister ? (
            <Button
              variant="primary"
              size="sm"
              isLoading={savingCaps}
              disabled={!capsDirty}
              onClick={() => void handleSaveCaps()}
            >
              Guardar caps
            </Button>
          ) : null}
        </div>

        <div className={tableScrollClass}>
          <table className={`${tableBodyClass} w-max`}>
            <thead className={theadStickyClass}>
              <tr>
                <th className={thServiceClass}>Servicio</th>
                {visiblePlans.map((plan) => (
                  <th key={plan.planAdminId} className={thPlanClass}>
                    {plan.planNombre}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CONEXION_SERVICES.map((service) => (
                <tr
                  key={service.id}
                  className="border-t border-slate-100 dark:border-gray-700"
                >
                  <td className={tdServiceClass}>
                    <span className="block truncate">{service.label}</span>
                  </td>
                  {visiblePlans.map((plan) => (
                    <td key={plan.planAdminId} className={tdPlanClass}>
                      <CapToggle
                        checked={plan[service.id]}
                        disabled={readOnly}
                        onToggle={() => togglePlanService(plan.planAdminId, service.id)}
                        label={`${service.label} — ${plan.planNombre}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-slate-100 dark:border-gray-700">
                <td className="px-4 py-1.5 text-slate-500">Push (con App)</td>
                {visiblePlans.map((plan) => (
                  <td key={plan.planAdminId} className="px-4 py-1.5 text-center text-xs text-slate-500">
                    <span className="inline-block w-11">{plan.appPatient ? 'Sí' : '—'}</span>
                  </td>
                ))}
              </tr>
              {PATIENT_ACTIONS.map((action) => (
                <tr
                  key={action.id}
                  className="border-t border-slate-100 dark:border-gray-700"
                >
                  <td className={tdServiceClass}>
                    <span className="block truncate">{action.label}</span>
                    {(action.id === 'patientCancel' || action.id === 'patientReschedule') && (
                      <span className="block text-[10px] leading-tight text-slate-400">
                        Solo plan Avanzado
                      </span>
                    )}
                  </td>
                  {visiblePlans.map((plan) => {
                    const policyLocked =
                      (action.id === 'patientCancel' || action.id === 'patientReschedule') &&
                      !plan.canConfigurePolicies;
                    return (
                    <td key={plan.planAdminId} className={tdPlanClass}>
                      <CapToggle
                        checked={plan[action.id]}
                        disabled={readOnly || !plan.appPatient || policyLocked}
                        onToggle={() => togglePlanAction(plan.planAdminId, action.id)}
                        label={`${action.label} — ${plan.planNombre}`}
                      />
                    </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="border-t border-slate-100 dark:border-gray-700">
                <td className={tdServiceClass}>
                  <span className="block truncate">Recordatorio WhatsApp</span>
                </td>
                {visiblePlans.map((plan) => (
                  <td key={plan.planAdminId} className={tdPlanClass}>
                    <select
                      value={plan.whatsappReminderTemplateMode === 'none' ? 'none' : 'plus'}
                      disabled={readOnly || !plan.channelWhatsapp}
                      onChange={(e) =>
                        setPlanMode(plan.planAdminId, e.target.value === 'none' ? 'none' : 'plus')
                      }
                      className={`${selectFixedClass} mx-auto`}
                    >
                      <option value="none">Solo recordatorio</option>
                      <option value="plus">Con confirmación</option>
                    </select>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      )}

      {activeTab === 'excedentes' && (
      <section className={cardFillClass}>
        <div className={headerBarClass}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Excedentes fuera del plan
          </h2>
          {canAdminister ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={savingOverage || overageLoading}
                onClick={addOverageTier}
              >
                Agregar tramo
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={savingOverage}
                disabled={!overageDirty || overageLoading}
                onClick={() => void handleSaveOverageTiers()}
              >
                Guardar tramos
              </Button>
            </div>
          ) : null}
        </div>
        {overageError ? (
          <p className="shrink-0 px-4 py-1.5 text-sm text-red-700 dark:text-red-300">{overageError}</p>
        ) : null}
        {overageLoading && overageTiers.length === 0 ? (
          <p className="px-4 py-4 text-sm text-slate-500">{t('common.loading')}</p>
        ) : overageTiers.length === 0 ? (
          <p className="px-4 py-4 text-sm text-slate-500">
            No hay tramos de excedente cargados. Agregá uno o corré el migrate del Panel.
          </p>
        ) : (
          <div className={tableScrollClass}>
            <table className={tableBodyClass}>
              <thead className={theadStickyClass}>
                <tr>
                  <th className="px-4 py-1.5 font-medium">Desde</th>
                  <th className="px-4 py-1.5 font-medium">Hasta</th>
                  <th className="px-4 py-1.5 font-medium">Precio por mensaje</th>
                  {canAdminister ? (
                    <th className="px-4 py-1.5 text-right font-medium">Acciones</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {overageTiers.map((row, index) => {
                  const isFirst = index === 0;
                  const isLast = index === overageTiers.length - 1;
                  const minQty = overageMinFor(overageTiers, index);
                  return (
                    <tr key={row.key} className="border-t border-slate-100 dark:border-gray-700">
                      <td className="px-4 py-1.5">
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          aria-label={`Desde del tramo ${index + 1}`}
                          value={String(minQty)}
                          disabled={readOnly || isFirst}
                          title={
                            isFirst
                              ? 'El primer tramo siempre empieza en 1 mensaje'
                              : 'Al cambiar el desde se ajusta el hasta del tramo anterior'
                          }
                          onChange={(e) => {
                            const raw = sanitizeDigitsInput(e.target.value);
                            setOverageTiers((prev) => withDesdeAt(prev, index, raw));
                          }}
                          className={`${fieldClass} max-w-[7.5rem]`}
                        />
                      </td>
                      <td className="px-4 py-1.5">
                        {isLast ? (
                          <input
                            type="text"
                            readOnly
                            disabled
                            value="Sin tope"
                            title="El último tramo queda abierto: cubre cualquier excedente por encima del anterior"
                            className={`${fieldClass} max-w-[7.5rem] text-slate-500`}
                          />
                        ) : (
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            aria-label={`Hasta del tramo ${index + 1}`}
                            value={row.hastaInput}
                            disabled={readOnly}
                            onChange={(e) => {
                              const hastaInput = sanitizeDigitsInput(e.target.value);
                              setOverageTiers((prev) =>
                                prev.map((x) => (x.key === row.key ? { ...x, hastaInput } : x)),
                              );
                            }}
                            className={`${fieldClass} max-w-[7.5rem]`}
                          />
                        )}
                      </td>
                      <td className="px-4 py-1.5">
                        <div className="relative max-w-[9rem]">
                          <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-slate-400">
                            $
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            aria-label={`Precio por mensaje del tramo ${index + 1}`}
                            value={row.precioInput}
                            disabled={readOnly}
                            onChange={(e) => {
                              const precioInput = sanitizeDigitsInput(e.target.value);
                              setOverageTiers((prev) =>
                                prev.map((x) => (x.key === row.key ? { ...x, precioInput } : x)),
                              );
                            }}
                            className={`${fieldClass} pl-6`}
                          />
                        </div>
                      </td>
                      {canAdminister ? (
                        <td className="px-4 py-1.5 text-right">
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={savingOverage || overageTiers.length === 1}
                            onClick={() => removeOverageTier(row.key)}
                          >
                            {t('common.delete')}
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      <ModalShell
        isOpen={isCreateTierOpen}
        onClose={() => {
          if (!creatingTier) setIsCreateTierOpen(false);
        }}
        title={t('pages.coofitConexion.tiers.createTitle')}
        subtitle={t('pages.coofitConexion.tiers.createSubtitle')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={creatingTier}
              onClick={() => setIsCreateTierOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="create-conexion-tier-form"
              isLoading={creatingTier}
            >
              {t('pages.coofitConexion.tiers.createSubmit')}
            </Button>
          </>
        }
      >
        <form id="create-conexion-tier-form" className="space-y-4" onSubmit={handleCreateTier}>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              {t('pages.coofitConexion.tiers.quotaLabel')}
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={newTierCupoInput}
              onChange={(event) => setNewTierCupoInput(sanitizeDigitsInput(event.target.value))}
              placeholder="Ej: 2000"
              className={fieldClass}
              disabled={creatingTier}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              {t('pages.coofitConexion.tiers.priceLabel')}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-slate-400">
                $
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={newTierPrecioInput}
                onChange={(event) =>
                  setNewTierPrecioInput(sanitizeDigitsInput(event.target.value))
                }
                placeholder="Ej: 180000"
                className={`${fieldClass} pl-6`}
                disabled={creatingTier}
              />
            </div>
          </div>
          <p className="rounded-md border border-teal-900/50 bg-teal-950/30 px-3 py-2 text-xs text-teal-100">
            {t('pages.coofitConexion.tiers.createHint')}
          </p>
        </form>
      </ModalShell>

      <ConfirmModal
        isOpen={tierToDelete != null}
        onClose={() => {
          if (!deletingTier) setTierToDelete(null);
        }}
        onConfirm={handleDeleteTier}
        title={t('pages.coofitConexion.tiers.deleteTitle')}
        message={t('pages.coofitConexion.tiers.deleteConfirm', { name: deleteTierName })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={deletingTier}
      >
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('pages.coofitConexion.tiers.deleteHint')}
        </p>
      </ConfirmModal>

    </div>
  );
};
